import { Provider } from "../../domain/Provider/schema"
import { apiService } from "../shared/apiService"
import appInsightsLogger from "../logging/appInsightsLogger"
import { hasActiveSignedDisclosure } from "../providerProfile/Disclosure/DisclosureV2"
import { referralTableDataEnums } from "../../domain/Referral/tableDataEnums"
import { ReferralPageContent } from "../../domain/CMS/schema"
import { capitalize } from "lodash"
import { Option } from "./ReferralInformation"

/**
 * Fetches the practice agreement and Knox Keene agreement in parallel and returns
 * the discount rate from whichever was most recently updated. Falls back to
 * baseRate if neither agreement has a valid discount rate.
 */
export async function resolveSupervisedCounselingRate(
  practiceId: string,
  baseRate: number,
): Promise<number> {
  try {
    const [practiceAgreementResult, knoxKeeneAgreementResult] =
      await Promise.allSettled([
        apiService.practice.get.PRACTICE_AGREEMENT_BY_PRACTICE_ID({
          practiceId,
        }),
        apiService.practice.get.PRACTICE_KNOX_KEENE_AGREEMENT_BY_PRACTICE_ID({
          practiceId,
        }),
      ])

    const candidates = [
      practiceAgreementResult.status === "fulfilled"
        ? practiceAgreementResult.value
        : null,
      knoxKeeneAgreementResult.status === "fulfilled"
        ? knoxKeeneAgreementResult.value
        : null,
    ].filter(
      (a): a is NonNullable<typeof a> =>
        !!a && !!a.lastUpdatedDateTime && !!a.discountRate,
    )

    if (candidates.length === 0) return baseRate

    candidates.sort(
      (a, b) =>
        new Date(b.lastUpdatedDateTime).getTime() -
        new Date(a.lastUpdatedDateTime).getTime(),
    )
    return candidates[0].discountRate
  } catch (error) {
    appInsightsLogger.traceWarning(
      `resolveSupervisedCounselingRate -- Failed to fetch agreement discount rate: ${error}`,
    )
    return baseRate
  }
}

/**
 * Returns providers eligible for transfer based on practice insurance requirements.
 * - If practice has authReleaseRequired (global insurance), filter to providers with active license
 * - Otherwise, filter to providers with both active license AND active individual liability insurance
 * - Always excludes the current referral's provider
 * - When disclosureResetEnabled, excludes providers without an active signed disclosure
 */
export function getEligibleTransferProviders(
  providers: Provider[],
  currentProviderId: string | undefined,
  authReleaseRequired: boolean,
  hasActiveGlobalInsurance: boolean,
): Provider[] {
  // If auth release required but no active global insurance, no providers are eligible
  if (authReleaseRequired && !hasActiveGlobalInsurance) {
    return []
  }

  // Filter to providers other than current with active license
  const providersWithActiveLicense = providers.filter(
    (p) =>
      p.id !== currentProviderId &&
      p.providerLicenses.some(
        (lic) =>
          lic.licenseStatusDisplayId === "active" ||
          lic.licenseStatusDisplayId === "expiring-soon",
      ),
  )

  // If auth release required (and has active global insurance), license is sufficient
  const eligibleProviders = authReleaseRequired
    ? providersWithActiveLicense
    : // Otherwise, also require active individual liability insurance
      providersWithActiveLicense.filter((p) =>
        p.liabilityInsurances.some(
          (ins) =>
            ins.liabilityInsuranceStatusDisplayId === "active" ||
            ins.liabilityInsuranceStatusDisplayId === "expiring-soon",
        ),
      )

  return eligibleProviders.filter(hasActiveSignedDisclosure)
}

const REFERRAL_STATUSES = referralTableDataEnums.referralStatuses
const REFERRAL_CLOSE_REASONS = referralTableDataEnums.closeReasons

export function getReferralDisplayStatus(
  status: string,
  content: ReferralPageContent,
): string {
  switch (status) {
    case REFERRAL_STATUSES.NEW:
      return capitalize(content.newStatusLabel || status)
    case REFERRAL_STATUSES.ACTIVE:
      return capitalize(content.activeStatusLabel || status)
    case REFERRAL_STATUSES.CLOSED:
      return capitalize(content.closedStatusLabel || status)
    default:
      return capitalize(status)
  }
}

export const getReferralCloseReason = (
  reason: string,
  content: ReferralPageContent,
): string => {
  switch (reason) {
    case REFERRAL_CLOSE_REASONS.ADMIN_CANCEL:
      return content.closeReasonAdminCancelText
    case REFERRAL_CLOSE_REASONS.PROVIDER_CANCEL:
      return content.closeReasonProviderCancelText
    case REFERRAL_CLOSE_REASONS.DECLINED:
      return content.closeReasonDeclinedText
    case REFERRAL_CLOSE_REASONS.EXPIRED:
      return content.closeReasonExpiredText
    case REFERRAL_CLOSE_REASONS.CLAIMED:
      return content.closeReasonClaimedText
    case REFERRAL_CLOSE_REASONS.TRANSFERRED:
      return content.closeReasonTransferredText
    case REFERRAL_CLOSE_REASONS.COMPLETED:
      return content.closeReasonCompletedText
    case REFERRAL_CLOSE_REASONS.ARCHIVED:
      return content.closeReasonArchivedText
    default:
      return reason
  }
}

const SESSION_TYPES = referralTableDataEnums.sessionTypes

/**
 * Returns the session method dropdown options available for a given provider.
 * Filters out "In Person" when the feature flag is on and the provider has no
 * physical locations (i.e. all location options have an empty value).
 */
export function getAvailableSessionMethodOptions(
  allOptions: Option[],
  locationOptions: Option[],
  isNoVirtualLocationEnabled: boolean | undefined,
): Option[] {
  if (!isNoVirtualLocationEnabled) return allOptions
  const hasPhysicalLocations = locationOptions.some((loc) => loc.value !== "")
  if (hasPhysicalLocations) return allOptions
  return allOptions.filter((opt) => opt.value !== SESSION_TYPES.IN_PERSON)
}
